import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Activity, RefreshCw, LogIn, LogOut, ShieldAlert, Monitor, Terminal,
  Package, RotateCcw, Cog, AlertTriangle, HardDrive, UserCheck, Info,
} from 'lucide-react';
import { formatWithTimezone } from '../utils/timezone';

const SOURCE_LABELS = {
  cipherone_portal: 'CipherOne Portal',
  ninja: 'NinjaOne',
  remote_command: 'Remote Command',
};

const CATEGORY_LABELS = {
  portal_auth: 'Portal Auth',
  device_session: 'Device Session',
  portal_identity: 'Ninja App Auth',
  software: 'Software',
  action: 'Action',
  security: 'Security',
  condition: 'Condition',
  reboot: 'Reboot',
  patch: 'Patch',
  backup: 'Backup',
  ticket: 'Ticketing',
  remote_command: 'Remote Command',
  other: 'Other',
};

const PAGE_SIZE = 15;
const EMPTY_FILTERS = { source: 'all', type: 'all', customer: '', device: '' };

function eventLabel(event) {
  const labels = {
    login_success: 'Signed in', login_failed: 'Sign-in failed', logout: 'Signed out',
  };
  const raw = String(event || '').trim();
  if (Object.prototype.hasOwnProperty.call(labels, raw)) return labels[raw];
  const words = raw.replace(/[_-]+/g, ' ');
  const readable = words === words.toUpperCase() ? words.toLowerCase() : words;
  return readable ? readable.charAt(0).toUpperCase() + readable.slice(1) : 'Activity';
}

function appendUniqueEvents(previous, incoming) {
  const seen = new Set(previous.filter((event) => event.id != null).map((event) => `${event.source}:${event.id}`));
  return [...previous, ...incoming.filter((event) => {
    if (event.id == null) return true;
    const key = `${event.source}:${event.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  })];
}

function iconFor(a) {
  if (a.source === 'cipherone_portal') {
    if (a.event === 'login_success') return LogIn;
    if (a.event === 'logout') return LogOut;
    return ShieldAlert;
  }
  if (a.source === 'remote_command') return Terminal;
  const map = {
    device_session: UserCheck, portal_identity: UserCheck, software: Package,
    action: Cog, security: ShieldAlert, condition: AlertTriangle, reboot: RotateCcw,
    patch: Package, backup: HardDrive, ticket: Info,
  };
  return map[a.category] || Monitor;
}

function outcomeClass(a) {
  if (a.outcome === 'failure') return 'co-ico-red';
  if (a.outcome === 'warning') return 'co-ico-orange';
  if (a.outcome === 'success') return 'co-ico-green';
  return 'co-ico-blue';
}

function outcomeBadge(a) {
  if (a.outcome === 'failure') return { cls: 'bg-red-100 text-red-800', text: a.severity || 'failure' };
  if (a.outcome === 'warning') return { cls: 'bg-amber-100 text-amber-800', text: a.severity || 'warning' };
  if (a.outcome === 'success') return { cls: 'bg-green-100 text-green-800', text: 'success' };
  return null;
}

export const RecentActivityPanel = () => {
  const [events, setEvents] = useState([]);
  const [freshness, setFreshness] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [customerInput, setCustomerInput] = useState('');
  const [deviceInput, setDeviceInput] = useState('');
  const requestId = useRef(0);

  const fetchFeed = useCallback(async (nextOffset, append) => {
    const currentRequest = ++requestId.current;
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(nextOffset));
    if (filters.source !== 'all') params.set('source', filters.source);
    if (filters.type !== 'all') params.set('event_type', filters.type);
    if (filters.customer) params.set('customer', filters.customer);
    if (filters.device) params.set('device', filters.device);
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLoadingMore(false);
        setEvents([]);
        setFreshness(null);
        setHasMore(false);
        setOffset(0);
      }
      setError(null);
      const { data } = await api.get(`/dashboard/activity-feed?${params.toString()}`);
      if (currentRequest !== requestId.current) return;
      setEvents((prev) => (append ? appendUniqueEvents(prev, data.events) : data.events));
      setFreshness(data.freshness);
      setHasMore(data.has_more);
      setOffset(nextOffset);
    } catch (e) {
      if (currentRequest !== requestId.current) return;
      setError({ message: 'Could not load activity. Please try again.', nextOffset, append });
    } finally {
      if (currentRequest === requestId.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    fetchFeed(0, false);
    return () => { requestId.current += 1; };
  }, [fetchFeed]);

  const applySearch = (event) => {
    event.preventDefault();
    setFilters((previous) => ({ ...previous, customer: customerInput.trim(), device: deviceInput.trim() }));
  };

  const resetFilters = () => {
    setCustomerInput('');
    setDeviceInput('');
    setFilters({ ...EMPTY_FILTERS });
  };

  return (
    <Card data-testid="recent-activity-panel">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <Activity className="h-5 w-5 text-[#37c9ee]" />
          Recent Activity
          <Button
            size="sm" variant="ghost" className="ml-auto h-8 px-2"
            onClick={() => fetchFeed(0, false)} data-testid="activity-refresh-btn"
            aria-label="Refresh recent activity"
            disabled={loading || loadingMore}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
        <div className="flex flex-wrap gap-2 pt-2">
          <Select value={filters.source} onValueChange={(source) => setFilters((previous) => ({ ...previous, source }))}>
            <SelectTrigger className="w-[180px] h-9" data-testid="activity-source-filter" aria-label="Activity source">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="cipherone_portal">CipherOne Portal</SelectItem>
              <SelectItem value="ninja">NinjaOne</SelectItem>
              <SelectItem value="remote_command">Remote Command</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.type} onValueChange={(type) => setFilters((previous) => ({ ...previous, type }))}>
            <SelectTrigger className="w-[180px] h-9" data-testid="activity-type-filter" aria-label="Event type">
              <SelectValue placeholder="All event types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All event types</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <form onSubmit={applySearch} className="flex flex-wrap items-end gap-2 pt-2" aria-label="Filter recent activity">
          <div className="space-y-1">
            <Label htmlFor="activity-customer-filter" className="text-xs">Customer</Label>
            <Input id="activity-customer-filter" value={customerInput} onChange={(event) => setCustomerInput(event.target.value)}
              placeholder="Customer name" className="h-9 w-[180px]" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="activity-device-filter" className="text-xs">Device</Label>
            <Input id="activity-device-filter" value={deviceInput} onChange={(event) => setDeviceInput(event.target.value)}
              placeholder="Device name or hostname" className="h-9 w-[220px]" />
          </div>
          <Button type="submit" size="sm" variant="outline" className="h-9">Apply filters</Button>
          <Button type="button" size="sm" variant="ghost" className="h-9" onClick={resetFilters}>Reset filters</Button>
        </form>
      </CardHeader>
      <CardContent>
        {/* Honest collection freshness / coverage */}
        {freshness && (
          <div className="flex flex-wrap gap-2 mb-3 text-[11px]" data-testid="activity-freshness">
            {Object.entries(freshness).map(([src, f]) => {
              const notConfigured = f.scope === 'not_configured';
              const count = f.recorded_events ?? 0;
              let text;
              if (notConfigured) {
                text = 'not configured';
              } else {
                text = `${count} recorded`;
                if (src === 'ninja' && f.ingestion_state) text += ` · ${f.ingestion_state}`;
                if (typeof f.quarantined === 'number' && f.quarantined > 0) text += ` · ${f.quarantined} unmapped`;
              }
              return (
                <span
                  key={src}
                  className={`px-2 py-1 rounded-md border ${notConfigured ? 'border-amber-300 text-amber-700' : 'border-[#193b4b] text-muted-foreground'}`}
                  title={f.note || f.windows_security_events || ''}
                >
                  {SOURCE_LABELS[src] || src}: {text}
                </span>
              );
            })}
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground text-center py-6" data-testid="activity-loading">Loading activity…</p>
        ) : <>
        {error && (
          <div className="text-center py-6" data-testid="activity-error">
            <p className="text-red-400 mb-2" role="alert">{error.message}</p>
            <Button size="sm" variant="outline" onClick={() => fetchFeed(error.nextOffset, error.append)} disabled={loadingMore}>Retry</Button>
          </div>
        )}
        {events.length === 0 ? (!error && (
          <p className="text-muted-foreground text-center py-6" data-testid="activity-empty">No activity matches these filters.</p>
        )) : (
          <div className="space-y-2" data-testid="activity-list">
            {events.map((a, i) => {
              const Icon = iconFor(a);
              const badge = outcomeBadge(a);
              return (
                <div
                  key={`${a.source}-${a.id}-${i}`}
                  className="flex items-start gap-3 p-2.5 rounded-lg co-soft"
                  data-testid={`activity-item-${i}`}
                >
                  <div className={`p-1.5 rounded-md shrink-0 ${outcomeClass(a)}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{eventLabel(a.event)}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5">{SOURCE_LABELS[a.source] || a.source}</Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5">{CATEGORY_LABELS[a.category] || a.category}</Badge>
                      {badge && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.text}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">{a.detail}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1">
                      {a.customer && <span>Customer: {a.customer}</span>}
                      {a.machine && <span>Machine: {a.machine}</span>}
                      {a.user && <span>User: {a.user}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0 text-[11px] text-muted-foreground whitespace-nowrap">
                    {formatWithTimezone(a.occurred_at, 'MMM d, h:mm a')}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>}

        {!loading && !error && hasMore && (
          <div className="pt-3 text-center">
            <Button
              size="sm" variant="outline" onClick={() => fetchFeed(offset + PAGE_SIZE, true)}
              disabled={loadingMore} data-testid="activity-load-more"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
